import {
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { UserManagementDTO } from '../../../dto/users/user-management.dto';
import { UserEmailManagementDialogComponent } from './user-email-management-dialog/user-email-management-dialog.component';
import { EmailConflictDialogComponent } from './email-conflict-dialog/email-conflict-dialog.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { AuthService } from '../../../services/auth/auth.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../ngrx/template/template.reducers';

@Component({
  selector: 'app-duna-users',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatBadgeModule,
    MatDialogModule,
    DynamicTableComponent,
  ],
  template: `
    @if (hasAccess) {
    <div class="duna-users-container">
      <mat-card class="users-header-card">
        <mat-card-header>
          <mat-card-title>
            <h1>Brugeradministration</h1>
          </mat-card-title>
          <mat-card-subtitle>
            Administrer brugere, deres emails og løs email konflikter
          </mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div class="header-actions">
            <button
              mat-raised-button
              color="accent"
              (click)="showConflicts()"
              [disabled]="loading"
            >
              <mat-icon>warning</mat-icon>
              Vis Konflikter @if (conflictCount !== null && conflictCount > 0) {
              <span class="conflict-badge"> ({{ conflictCount }}) </span>
              }
            </button>
          </div>
        </mat-card-content>
      </mat-card>
      @if (loadError) {
      <mat-card class="error-card">
        <mat-card-content>
          <mat-icon color="warn">error_outline</mat-icon>
          <span>{{ loadError }}</span>
        </mat-card-content>
      </mat-card>
      }
      <mat-card class="users-table-card">
        <mat-card-content>
          <app-dynamic-table [dynamicInput]="userTableDynamicInput">
          </app-dynamic-table>
        </mat-card-content>
      </mat-card>
    </div>
    } @else {
    <div class="duna-users-container">
      <mat-card class="error-card">
        <mat-card-content>
          <mat-icon color="warn">lock</mat-icon>
          <span
            >Kun superadmin og orgadmin har adgang til
            brugeradministration.</span
          >
        </mat-card-content>
      </mat-card>
    </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .duna-users-container {
        padding: 20px;
        max-width: 1400px;
        margin: 0 auto;
      }

      .users-header-card {
        margin-bottom: 20px;
      }

      .users-header-card mat-card-header {
        margin-bottom: 16px;
      }

      .users-header-card h1 {
        margin: 0;
        font-size: 24px;
        font-weight: 500;
      }

      .header-actions {
        display: flex;
        gap: 12px;
        margin-top: 12px;
      }

      .header-actions button {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .conflict-badge {
        margin-left: 4px;
        font-weight: 600;
        color: #ff5722;
      }

      .users-table-card {
        min-height: 400px;
      }

      .users-table-card mat-card-content {
        padding: 0;
      }

      .error-card {
        margin-bottom: 16px;
      }

      .error-card mat-card-content {
        display: flex;
        align-items: center;
        gap: 12px;
        color: #c62828;
        padding: 16px;
      }
    `,
  ],
})
export class DunaUsersComponent implements OnInit, OnDestroy {
  loading = false;
  conflictCount: number | null = null;
  loadError: string | null = null;
  hasAccess = false;
  private isSuperadmin = false;
  readonly userTableDynamicInput = {
    dataSource: { type: 'text', value: 'duna_users' },
    enableSearch: { type: 'boolean', value: true },
  };
  private destroy$ = new Subject<void>();

  constructor(
    private componentDataSourceService: ComponentDataSourceService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private authService: AuthService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.isSuperadmin = this.authService.hasAnyRoleSync(['superadmin']);
    this.hasAccess = this.isSuperadmin || this.authService.hasAnyRoleSync(['orgadmin']);

    window.addEventListener(
      'duna-users-row-click',
      this.handleRowClickEvent.bind(this) as EventListener
    );

    if (this.hasAccess) {
      this.loadConflictCount();
      this.store
        .select((state) => state.data['duna_users'])
        .pipe(
          takeUntil(this.destroy$),
          filter((dataState) => !!dataState && !dataState.loading)
        )
        .subscribe((dataState) => {
          this.loadError = dataState?.data?.error ?? null;
          this.cdr.detectChanges();
        });
    }
  }

  handleRowClickEvent(event: CustomEvent): void {
    const user = event.detail as UserManagementDTO;
    this.onUserRowClick(user);
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'duna-users-row-click',
      this.handleRowClickEvent.bind(this) as EventListener
    );
    this.destroy$.next();
    this.destroy$.complete();
  }

  refreshData(): void {
    this.loading = true;
    this.loadConflictCount();
    setTimeout(() => {
      this.loading = false;
      this.cdr.detectChanges();
    }, 100);
  }

  loadConflictCount(): void {
    const url = `${environment.apiUrl}/users/manage/conflicts?page=1&pageSize=1`;
    this.http.get<any>(url).subscribe({
      next: (response) => {
        if (response && response.meta) {
          this.conflictCount =
            response.meta.totalItems ?? response.meta.total ?? 0;
        } else {
          this.conflictCount = 0;
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading conflict count:', error);
        this.conflictCount = 0;
        this.cdr.detectChanges();
      },
    });
  }

  showConflicts(): void {
    const dialogRef = this.dialog.open(EmailConflictDialogComponent, {
      width: '90vw',
      minWidth: '1200px',
      maxWidth: '1600px',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refresh) {
        this.refreshData();
      }
      this.loadConflictCount();
    });
  }

  onUserRowClick(user: UserManagementDTO): void {
    const dialogRef = this.dialog.open(UserEmailManagementDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: {
        user: user,
        readOnly:
          (user.has_protected_role ?? false) && !this.isSuperadmin,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refresh) {
        this.refreshData();
      }
      this.loadConflictCount();
    });
  }
}
